<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal fade show" tabindex="-1" aria-labelledby="modalLabel" aria-hidden="true">
      <div class="modal-backdrop" @click="handleBackdropClick"></div>
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalLabel">{{ title }}</h5>
            <div class="modal-buttons">
              <button v-if="showDeleteButton" 
                      class="modal-delete-btn" 
                      @click="$emit('delete')"
                      title="Delete">
                <i class="delete-icon">🗑</i>
              </button>
              <button type="button" class="modal-close-btn" @click="closeModal" aria-label="Close">
                <span>×</span>
              </button>
            </div>
          </div>
          <div class="modal-body">
            <!-- Слот для динамического контента -->
            <slot></slot>
          </div>
          <div class="modal-footer">
            <!-- Слот для динамических кнопок -->
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  props: {
    isVisible: { type: Boolean, required: true },
    title: { type: String, required: true },
    showDeleteButton: {
      type: Boolean,
      default: false
    }
  },
  inheritAttrs: false,
  data() {
    return {
      scrollPosition: 0
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
      setTimeout(() => {
        window.scrollTo(0, this.scrollPosition);
      }, 0);
    },
    getScrollbarWidth() {
      // Создаем временный div для измерения ширины скроллбара
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      document.body.appendChild(outer);

      const inner = document.createElement('div');
      outer.appendChild(inner);

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode.removeChild(outer);

      return scrollbarWidth;
    },
    handleBackdropClick(event) {
      if (event.target.classList.contains('modal-backdrop')) {
        this.closeModal();
      }
    },
    handleEscKey(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    },
    addEscListener() {
      document.addEventListener('keydown', this.handleEscKey);
    },
    removeEscListener() {
      document.removeEventListener('keydown', this.handleEscKey);
    }
  },
  mounted() {
    if (this.isVisible) {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      const scrollbarWidth = this.getScrollbarWidth();
      document.body.classList.add('modal-open');
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      this.addEscListener();
    }
  },
  beforeUnmount() {
    document.body.classList.remove('modal-open');
    document.body.style.paddingRight = '';
    this.removeEscListener();
    window.scrollTo(0, this.scrollPosition);
  }
};
</script>

<style>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex !important;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  animation: modalFadeIn 0.3s ease;
  padding-top: 5vh;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9998;
  animation: backdropFadeIn 0.5s ease forwards;
  backdrop-filter: blur(0px);
}

.modal-dialog {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  z-index: 10000;
  transform: translateY(0);
  animation: modalSlideIn 0.3s ease;
  max-height: calc(100vh - 3.5rem);
  overflow-y: auto;
}

.modal-content {
  background: var(--my-dark);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-body {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  color: var(--my-light);
  font-size: 14px;
  line-height: 1.6;
}

.modal-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-title {
  color: var(--my-light);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.modal-close-btn {
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close-btn span {
  color: var(--my-light);
  font-size: 28px;
  line-height: 1;
  opacity: 0.7;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.modal-close-btn:hover span {
  opacity: 1;
}

/* Анимации */
@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes backdropFadeIn {
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(5px);
  }
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Стили для форм внутри модального окна */
.modal-content .form-group {
  margin-bottom: 1.5rem;
}

.modal-content label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--my-light);
  font-weight: 500;
  font-size: 14px;
}

.modal-content .form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--my-dark);
  transition: all 0.3s ease;
}

.modal-content .form-control:focus {
  outline: none;
  border-color: var(--my-links);
  box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
  background: rgba(255, 255, 255, 1);
}

.modal-content .btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  width: 100%;
  text-align: center;
}

.modal-content .btn-success {
  background: var(--my-links);
  border: none;
  margin-bottom: 5px;
}

.modal-content .btn-success:hover {
  background: var(--my-accent);
  transform: translateY(-1px);
}

.modal-content .btn-danger {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
  margin-top: 5px;
}

.modal-content .btn-danger:hover {
  background: rgba(220, 53, 69, 0.2);
  transform: translateY(-1px);
}

.modal-open {
  overflow: hidden;
}

/* Стилизация скроллбара для модального окна */
.modal-dialog::-webkit-scrollbar {
  width: 8px;
}

.modal-dialog::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.modal-dialog::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.modal-dialog::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Предотвращаем сдвиг фиксированных элементов при открытии модального окна */
.modal-open .navbar-container,
.modal-open .bottom-nav {
  padding-right: inherit;
}

.modal-open {
  overflow: hidden;
}

/* Стили для textarea */
.modal-content .form-control-description {
  min-height: 100px;
  resize: vertical;
  background: rgba(255, 255, 255, 0.9);
  color: var(--my-dark);
}

.modal-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.modal-delete-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc3545;
  transition: all 0.2s ease;
}

.modal-delete-btn:hover {
  transform: scale(1.1);
  color: #c82333;
}

.delete-icon {
  font-style: normal;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  color: #666;
}
</style>
